import { formatMoney } from '@theme/money-formatting';

/**
 * Free shipping progress bar with a threshold per Brazilian region.
 *
 * The customer's state (UF) comes from IP geolocation and is replaced by the UF of the CEP
 * typed in "Consultar frete" (more accurate). Liquid renders the default threshold; this
 * element re-renders the message/bar with the customer's region threshold on the client.
 */

const STORAGE_KEY = 'pasquini:free-shipping-uf';
const REGION_CHANGE_EVENT = 'free-shipping:region-change';
const CEP_INPUT_SELECTOR = '.cart-shipping-consult__input, .product-shipping-consult__input';

const HOUR = 60 * 60 * 1000;
const CEP_TTL = 30 * 24 * HOUR;
const IP_TTL = 24 * HOUR;
const IP_FAILURE_TTL = 6 * HOUR;

/** @type {Record<string, string>} */
const REGION_BY_UF = {
  PR: 'sul', RS: 'sul', SC: 'sul',
  ES: 'sudeste', MG: 'sudeste', RJ: 'sudeste', SP: 'sudeste',
  DF: 'centro_oeste', GO: 'centro_oeste', MT: 'centro_oeste', MS: 'centro_oeste',
  AL: 'nordeste', BA: 'nordeste', CE: 'nordeste', MA: 'nordeste', PB: 'nordeste',
  PE: 'nordeste', PI: 'nordeste', RN: 'nordeste', SE: 'nordeste',
  AC: 'norte', AM: 'norte', AP: 'norte', PA: 'norte', RO: 'norte', RR: 'norte', TO: 'norte',
};

/**
 * @typedef {{ uf: string | null, source: 'cep' | 'ip', exp: number }} CachedUf
 */

/** @returns {CachedUf | null} */
function readCachedUf() {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (cached && typeof cached.exp === 'number' && Date.now() < cached.exp) return cached;
  } catch {}
  return null;
}

/**
 * @param {string | null} uf
 * @param {'cep' | 'ip'} source
 * @param {number} ttl
 */
function setUf(uf, source, ttl) {
  const normalized = uf ? uf.toUpperCase() : null;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ uf: normalized, source, exp: Date.now() + ttl }));
  } catch {}

  if (normalized === currentUf) return;
  currentUf = normalized;
  document.dispatchEvent(new CustomEvent(REGION_CHANGE_EVENT, { detail: { uf: normalized, source } }));
}

let currentUf = readCachedUf()?.uf ?? null;

/** @type {Promise<void> | null} */
let ipLookup = null;

/**
 * Geolocates the UF by IP, only when there's no valid cached UF (a CEP-based UF always wins).
 */
function lookupUfByIp() {
  if (readCachedUf()) return;
  if (ipLookup) return;

  ipLookup = fetch('https://ipwho.is/?fields=success,country_code,region_code')
    .then((response) => response.json())
    .then((data) => {
      if (readCachedUf()?.source === 'cep') return;
      const uf = data?.success && data.country_code === 'BR' && data.region_code ? String(data.region_code) : null;
      setUf(uf, 'ip', uf ? IP_TTL : IP_FAILURE_TTL);
    })
    .catch(() => setUf(null, 'ip', IP_FAILURE_TTL));
}

/** @type {string | null} */
let lastCep = null;

/**
 * Resolves the UF of the CEP typed in "Consultar frete" once it has 8 digits.
 * Delegated on document because the cart drawer markup is re-rendered on cart updates.
 * @param {Event} event
 */
function onCepInput(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches(CEP_INPUT_SELECTOR)) return;

  const cep = input.value.replace(/\D/g, '');
  if (cep.length !== 8 || cep === lastCep) return;
  lastCep = cep;

  fetch(`https://viacep.com.br/ws/${cep}/json/`)
    .then((response) => response.json())
    .then((data) => {
      if (data?.uf && REGION_BY_UF[String(data.uf).toUpperCase()]) setUf(data.uf, 'cep', CEP_TTL);
    })
    .catch(() => {});
}

/** @type {Promise<number | null> | null} */
let cartSubtotalFetch = null;

/** @returns {Promise<number | null>} */
function fetchCartSubtotal() {
  cartSubtotalFetch ??= fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
    .then((response) => response.json())
    .then((cart) => Number(cart.items_subtotal_price))
    .catch(() => null)
    .finally(() => {
      cartSubtotalFetch = null;
    });

  return cartSubtotalFetch;
}

/**
 * Markup contract (children):
 * - [data-fs-success] / [data-fs-remaining]: toggled with `hidden` depending on the state
 * - [data-fs-amount]: remaining amount text
 * - [data-fs-fill]: progress bar fill (width %)
 *
 * Attributes: data-subtotal, data-thresholds (JSON, cents per region + default),
 * data-money-format, data-currency, data-success-class (optional),
 * data-live-cart (optional: refreshes the subtotal on cart updates, for blocks outside the cart drawer).
 */
class FreeShippingProgress extends HTMLElement {
  /** Re-renders after the cart drawer morph rewrites this markup with the server (default) threshold. */
  #observer = new MutationObserver(() => this.#render());

  connectedCallback() {
    document.addEventListener(REGION_CHANGE_EVENT, this.#render);
    if (this.hasAttribute('data-live-cart')) {
      document.addEventListener('shopify:cart:lines-update', this.#onCartUpdate);
    }

    this.#render();
    lookupUfByIp();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    document.removeEventListener(REGION_CHANGE_EVENT, this.#render);
    document.removeEventListener('shopify:cart:lines-update', this.#onCartUpdate);
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(() => fetchCartSubtotal())
      .then((subtotal) => {
        if (subtotal != null && !Number.isNaN(subtotal)) this.dataset.subtotal = String(subtotal);
      })
      .catch(() => {});
  };

  /** @returns {number} Threshold in cents for the current UF, falling back to the default. */
  get #threshold() {
    /** @type {Record<string, number>} */
    let thresholds = {};
    try {
      thresholds = JSON.parse(this.dataset.thresholds || '{}');
    } catch {}

    const region = currentUf ? REGION_BY_UF[currentUf] : undefined;
    const regionThreshold = region ? thresholds[region] : undefined;

    return Number(regionThreshold ?? thresholds.default ?? 0);
  }

  #render = () => {
    this.#observer.disconnect();

    const threshold = this.#threshold;
    const subtotal = Number(this.dataset.subtotal) || 0;

    this.hidden = threshold <= 0;

    if (threshold > 0) {
      const remaining = Math.max(0, threshold - subtotal);
      const success = remaining === 0;

      for (const element of this.querySelectorAll('[data-fs-success]')) {
        /** @type {HTMLElement} */ (element).hidden = !success;
      }
      for (const element of this.querySelectorAll('[data-fs-remaining]')) {
        /** @type {HTMLElement} */ (element).hidden = success;
      }

      const amount = this.querySelector('[data-fs-amount]');
      const formatted = formatMoney(remaining, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'BRL');
      if (amount && amount.textContent !== formatted) amount.textContent = formatted;

      const fill = /** @type {HTMLElement | null} */ (this.querySelector('[data-fs-fill]'));
      if (fill) fill.style.width = `${Math.min(100, (subtotal * 100) / threshold)}%`;

      if (this.dataset.successClass) this.classList.toggle(this.dataset.successClass, success);
    }

    this.#observer.observe(this, { attributes: true, childList: true, characterData: true, subtree: true });
  };
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
  document.addEventListener('input', onCepInput);
}
